<template>
    <div class="settings__page">
        <div>
            <template v-if="fetchUserLoading">
                <PageSpinner />
            </template>
    
            <template v-else-if="fetchUserErr">
                <div class="mt--40">
                    <EmptyPage state="error" @action="handleFetchProfile" />
                </div>
            </template>
    
            <template v-else>
                <div>
                    <div class="mb--35 mt--0">
                        <div class="flex align-items-center justify-content-between">
                            <CustomText size="lg" color="gray-800" transform="capitalize" weight="semi--bold">
                                Account settings
                            </CustomText>

                            <div class="flex align-items-center">
                                <BaseButton
                                    label="Edit profile"
                                    prefix-icon
                                    icon-name="edit"
                                    class="mr--5"
                                    @submit="openAccountModal('profile')"
                                />

                                <BaseButton
                                    variant-style="secondary" 
                                    variant="outline" 
                                    label="Export profile"
                                    prefix-icon
                                    icon-name="export"
                                    class="mr--5"
                                    @submit="modalAction = 'showCurateProfileModal'"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- <template v-if="view === 'account_details'"> -->
                    <div class="settings__account__info border-b-light">
                        <div>
                            <div>
                                <div class="mb--35">
                                    <div class="flex align-items-center">
                                        <CustomAvatar
                                            border-style="solid" 
                                            width="100px" 
                                            :initials="avatarInitials" 
                                            :src="profileUrlValue"
                                            class="text--lg"
                                            show-upload
                                            :loading="uploadProfileUrlLoading === 'loading'"
                                            @update:files="uploadProfileImage" 
                                        />
                                        <div v-if="profileUrlValue" class="ml--30">
                                            <BaseButton
                                                variant="outline" 
                                                variant-style="warning" 
                                                label="Remove image"
                                                :disabled="uploadProfileUrlLoading === 'loading'"
                                                @submit="removeProfileImage"
                                            />
                                        </div>
                                    </div>

                                    <p v-if="!profileUrlValue" class="form-text text--xs">
                                        (Max image size is 1MB)
                                    </p>
                                </div>
    
                                <div>
                                    <div>
                                        <FormLabel 
                                            title="Full name" 
                                            html-for="info" 
                                            label-type="menu" 
                                            class="mb--0 text--semi--bold"
                                            info-type="info"
                                            no-margin-bottom
                                        />
                                    </div>
            
                                    <div class="mb--15 mt--5">                            
                                        <p class="text--sm text-gray-600">
                                            {{ profile.fullName }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div>
                                    <FormLabel 
                                        title="Brand name" 
                                        html-for="info" 
                                        label-type="menu" 
                                        class="mb--0 text--semi--bold"
                                        info-type="info"
                                        no-margin-bottom
                                    />
                                </div>
        
                                <div class="mb--15 mt--5">                            
                                    <p class="text--sm text-gray-600">
                                        {{ profile.brandName || '-' }}
                                    </p>
                                </div>
                            </div>
        
                            <div>
                                <FormLabel 
                                    title="Bio" 
                                    html-for="info" 
                                    label-type="menu" 
                                    class="mb--0 text--semi--bold" 
                                    no-margin-bottom
                                />
                            </div>
        
                            <div class="mb--15 mt--5">                            
                                <p class="text--sm text-gray-600 lh-base">
                                    {{ profile.bio || '-' }}
                                </p>
                            </div>
        
                            <div class="mb--20">
                                <FormLabel 
                                    title="Occupation" 
                                    html-for="info" 
                                    label-type="menu" 
                                    class="mb--0 text--semi--bold" 
                                    no-margin-bottom
                                />
                                <div class="mt--5">                            
                                    <p class="text--sm text--capitalize text-gray-600">
                                        {{ profile.occupation || '-' }}
                                    </p>
                                </div>
                            </div>
        
                            <div class="mb--15 m-w-50">
                                <div class="flex align-items-center">
                                    <FormLabel title="Skills" html-for="info" label-type="menu" class="text--semi--bold mr--15 pb--0 mb--0" />
                                    <BaseButton 
                                        label="Manage skills"
                                        medium
                                        size="md"
                                        class="p--0 text--sm"
                                        variant="ghost"
                                        @submit="modalAction = 'showSkillModal'"
                                    />
                                </div>
        
                                <div v-if="profile.skills && profile.skills.length" class="project__tags mt--15">
                                    <div 
                                        v-for="skill in profile.skills" :key="skill" 
                                        class="mr--10 tag default tag--grey text--xs text--semi--bold"
                                    >
                                        {{ skill }}
                                    </div>
                                </div>
        
                                <template v-else>
                                    <p class="project__item__desc">
                                        N/A
                                    </p>
                                </template>
                            </div>
                        </div>
                    </div>
    
                    <div class="border-b-light mt--15">
                        <FormLabel title="Contact details" html-for="info" label-type="title" />
    
                        <div class="flex">
                            <div class="flex-1">
                                <div class="mt--5 mb--15">
                                    <div class="invoice__client--bio stack">
                                        <p>
                                            Phone
                                        </p>
                                        <p>
                                            {{ profile.phoneNumber || '-' }}
                                        </p>
                                    </div>
                                    <div class="invoice__client--bio stack">
                                        <p>
                                            Address
                                        </p>
                                        <p>
                                            {{ profile.address || '-' }}
                                        </p>
                                    </div>
                                    <div class="invoice__client--bio stack">
                                        <p>
                                            Country
                                        </p>
                                        <p>
                                            {{ profile.country || '-' }}
                                        </p>
                                    </div>
                                    <div class="invoice__client--bio stack">
                                        <p>
                                            Zipcode
                                        </p>
                                        <p>
                                            {{ profile.zipCode || '-' }}
                                        </p>
                                    </div>
                                </div>
                            </div>
    
                            <div class="flex-1">
                                <div class="flex align-items-center justify-content-between">
                                    <div>
                                        <FormLabel title="Email address" html-for="info" label-type="menu" class="text--semi--bold" />
                                        <p class="text--sm text-gray-600">
                                            {{ profile.email }}
                                        </p>
                                    </div>
                                    <BaseButton
                                        variant="outline" 
                                        variant-style="secondary" 
                                        label="Change email"
                                        @submit="openAccountModal('emailSettings')"
                                    />
                                </div>
                            </div>
                        </div>                        
    
                        <div class="mb--15 mt--15 m-w-50 flex-1">
                            <div class="flex align-items-center">
                                <FormLabel title="Socials" html-for="info" label-type="menu" class="text--semi--bold mr--15" />
                                <BaseButton 
                                    label="+ Add social link"
                                    medium
                                    size="md"
                                    class="p--0 text--sm"
                                    variant="ghost"
                                    @submit="openLinkModal(null, 'add_socials')"
                                />
                            </div>
                                    
                            <div v-if="profile.socials && profile.socials.length" class="flex space-x-3 flex-wrap">
                                <div v-for="item in profile.socials" :key="item._id" class="settings__account__socials__icons">
                                    <div class="dropdown">
                                        <div 
                                            id="socialDropdown" 
                                            class="p-2 border border-dark-subtle cursor-pointer rounded-full transition duration-150 mr--15"
                                            aria-expanded="false"
                                            data-bs-toggle="dropdown" 
                                            data-bs-auto-close="true" 
                                        >
                                            <IconSvg 
                                                class="h-5 w-5 text-gray-700" 
                                                fill="#868788" 
                                                :name="item.name" 
                                                width="24px" 
                                                height="24px" 
                                            />
                                        </div>
        
                                        <ul id="socialDropdownList" class="dropdown-menu dropdown-menu--tag" aria-labelledby="socialDropdown">
                                            <li class="dropdown-item flex align-items-center text-gray-600 cursor-pointer text--xs" @click="openLinkNewPage(item.url)">
                                                <IconSvg class="mr--5" name="show" fill="#788490" /> 
                                                <span>View in new tab</span>
                                            </li>
                                            <li class="dropdown-item flex align-items-center text-gray-600 cursor-pointer text--xs" @click="openLinkModal(null, 'edit_socials')">
                                                <IconSvg class="mr--5" name="edit" fill="#788490" /> 
                                                <span>Edit link</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> 
                        
                    <div class="border-b-light mt--15">
                        <div class="col-12 flex align-items-end justify-content-between">
                            <FormLabel title="Portfolio links" html-for="info" label-type="title" />
                        </div>
    
                        <div class="col-12 flex align-items-end justify-content-between mt--20" :class="{ 'mb--25': !socialsArrayExists}">
                            <div class="form__row">
                                <FormInput 
                                    id="linkName" 
                                    v-model="linkForm.name"
                                    size="sm" 
                                    wrapper-class="flex-1"
                                    info-type="required"
                                    no-margin-bottom
                                    html-for="linkName" 
                                    label="Link name"
                                />
    
                                <FormInput 
                                    id="linkUrl" 
                                    v-model="linkForm.url"
                                    size="sm" 
                                    wrapper-class="flex-1"
                                    info-type="required"
                                    no-margin-bottom
                                    html-for="linkUrl" 
                                    label="Link URL"
                                />
                            </div>
                                
                            <BaseButton 
                                variant="outline" 
                                variant-style="secondary"
                                label="+ Add link"
                                :disabled="!linkForm.url || !linkForm.name"
                                :loading="linkState === 'add_link_loading'"
                                class="ml--10 max-h-fit"
                                @submit="addPortfolioLink"
                            />
                        </div>
    
                        <div v-if="profile.links && socialsArrayExists" class="mt--40 mb--25">
                            <div 
                                v-for="link in profile.links" 
                                :key="link._id" 
                                class="mb--10 flex align-items-center justify-content-between w-100 flex-wrap"
                                :class="{ 'disabled': linkForm?._id === link._id }"
                            >
                                <a class="project__item__title text--medium mt--0 text--sm text-link text--underline" :href="link.url" target="_blank">
                                    {{ link.name }}
                                </a>
                                    
                                <div class="flex align-items-center">
                                    <BaseButton 
                                        variant="outline" 
                                        prefix-icon
                                        icon-name="edit"
                                        class="mr--10"
                                        @submit="openLinkModal(link, 'edit_links')"
                                    />
                                    <BaseButton 
                                        variant="outline" 
                                        prefix-icon
                                        icon-name="close"
                                        variant-style="warning"
                                        :loading="linkState === 'edit_loading' && linkIdToDelete === link._id"
                                        @submit="deleteLink(link._id)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                        
                    <div v-if="!profile.googleId" class="settings__sub__header text--sm mt--15">
                        <FormLabel title="Password" html-for="info" label-type="menu" class="text--semi--bold" />
                        <BaseButton
                            class="pl--0 pr--0" 
                            variant="ghost" 
                            prefix-icon
                            bold
                            icon-name="lock"
                            label="Change password"
                            @submit="togglePasswordUpdateModal"
                        />
                    </div>

                    <div class="mt--30">
                        <FormLabel title=" Delete your account" html-for="info" label-type="menu" class="mb--0" />
                        <p class="text--sm text-gray-600 mt--3">
                            Clicking the button below will delete all your data including your files and history.
                        </p>
    
                        <div class="row__item relative pl--0 mt--20">
                            <BaseButton 
                                :loading="deleteLoading" 
                                label="Delete account" 
                                variant="warning" 
                                variant-style="secondary"
                                @submit="toggleDelete"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <ChangePassword :show-modal="showPasswordChangeModal" @cancel="togglePasswordUpdateModal" />

        <UpdateProfileModal 
            :show-modal="modalAction === 'showAccountModal'"
            :current-selection="currentAccountModalSelection" 
            :loading="uploadProfileUrlLoading === 'loading'"
            @submit="handleUpdateProfile"
            @cancel="closeAccountModal"
        />

        <ConfirmDeletion
            type="account" custom-msg
            :show-modal="showDeleteModal"
            :loading="deleteLoading" 
            @cancel="toggleDelete" 
            @delete="handleDeleteProfile"
        >
            <template #custom-msg>
                <p class="text--sm">
                    Are you sure you want to delete your account? <strong>This action cannot be undone.</strong>
                </p>
            </template>
        </ConfirmDeletion>

        <CurateProfileModal :show-modal="modalAction === 'showCurateProfileModal'" @cancel="modalAction = ''" />

        <MainModal 
            :show-modal="modalAction === 'showLinkModal'"
            size="md" 
            :title="linkType ? 'Manage web link' : 'Manage social link'" 
            @close="closeEditLinkModal"
        >
            <template #body>
                <form @submit.prevent="">
                    <div class="form__row">
                        <template v-if="linkType === 'edit_links'">
                            <FormInput 
                                id="linkName" 
                                v-model="modalLinkForm.name"
                                size="sm" 
                                wrapper-class="flex-1"
                                info-type="required"
                                no-margin-bottom
                                label="Link name"
                            />
                        </template>
                        <template v-else>
                            <div class="flex-1 mr--5">
                                <FormLabel title="Link name" html-for="info" />
                                <select v-model="modalLinkForm.name" class="form-select form-select-sm text--sm filter-input text--capitalize">
                                    <option v-for="social in socials" :key="social" :value="social">
                                        {{ social }}
                                    </option>
                                </select>
                            </div>
                        </template>
                        
                        <FormInput 
                            id="linkUrl" 
                            v-model="modalLinkForm.url"
                            size="sm" 
                            wrapper-class="flex-1"
                            info-type="required"
                            no-margin-bottom
                            label="Link URL"
                        />
                    </div>
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--5" 
                        label="Cancel" 
                        variant="outline" 
                        :disabled="disabledModalLinkBtn" 
                        @submit="closeEditLinkModal"
                    />
                    <BaseButton 
                        type="submit" 
                        :loading="disabledModalLinkBtn" 
                        :disabled="!modalLinkForm.name || !modalLinkForm.url" 
                        label="Update" 
                        @submit="handleModalLinkSubmit"
                    />
                </div>
            </template>
        </MainModal>

        <MainModal 
            :show-modal="modalAction === 'showSkillModal'"
            size="sm" 
            title="Manage skills" 
            @close="modalAction = ''"
        >
            <template #body>
                <form @submit.prevent="">
                    <div class="">
                        <FormLabel title="Select one or more skills" />
                        <!-- <CustomDropdown 
                            id="rateCardDropdown" 
                            v-model="form.rateCards" 
                            :options="rateCards"
                            list-type="rate cards" 
                            index-label="title" 
                            multiple 
                            class="pl--3"
                            :loading="dropdownLoading"
                            @search="searchRateCards" 
                            @select="addRateCard" 
                        /> -->
                        <vue-multi-select 
                            v-model="skills" 
                            :options="skillOptions"
                            tag-placeholder="Add this as new skill" 
                            placeholder="Search or add a skill" 
                            multiple 
                            :show-labels="false" 
                            taggable 
                            :close-on-select="false" 
                            label="" track-by=""
                            @tag="addSkill" 
                            @search-change="searchSkills"
                        >
                            <template #option="props">
                                <span class="tags--item--text">{{ props.option }}</span>
                            </template>
                        </vue-multi-select>
                    </div>
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--5" 
                        label="Cancel" 
                        variant="outline" 
                        :disabled="manageSkillsLoading" 
                        @submit="modalAction = ''"
                    />
                    <BaseButton 
                        type="submit" 
                        :loading="manageSkillsLoading" 
                        label="Update skills" 
                        @submit="manageSkills"
                    />
                </div>
            </template>
        </MainModal>
    </div>
</template>

<script>
import {
    BaseButton,
    PageSpinner,
    ConfirmDeletion,
    EmptyPage,
    ChangePassword,
    FormLabel,
    IconSvg,
    CustomAvatar,
    UpdateProfileModal,
    CustomText,
    CurateProfileModal,
    FormInput,
    MainModal,
    DropDownButton,
} from "@/components/ui/index";
import { socials } from "@/utils/dummy";
import { debounce } from "@/utils/helpers";

export default {
    name: "SettingsAccount",

    components: {
        ChangePassword,
        BaseButton,
        PageSpinner,
        ConfirmDeletion,
        EmptyPage,
        CustomAvatar,
        CustomText,
        IconSvg,
        FormLabel,
        UpdateProfileModal,
        CurateProfileModal,
        FormInput,
        MainModal,
    },

    data() {
        return {
            socials,
            showPasswordChangeModal: false,
            deleteLoading: false,
            fetchUserLoading: false,
            fetchUserErr: false,
            fetchUserErrMsg: "",
            showDeleteModal: false,
            currentAccountModalSelection: "",
            uploadProfileUrlLoading: "default",
            form: {
                profileUrl: "",
            },
            signedUrlRes: null,
            linkForm: {
                url: "",
                name: "",
            },
            linkState: "default",
            linkIdToDelete: "",
            modalLinkForm: {
                url: "",
                name: "",
            },
            linkType: "socials",
            manageSkillsLoading: false,
            skills: [],
            skillOptions: [],
            modalAction: "",
            view: "account_details",
        };
    },

    computed: {
        profile() {
            return this.$store.getters["auth/profile"];
        },

        twoFactorAuth() {
            return this.$store.getters["settings/twoFactorAuth"];
        },

        avatarInitials() {
            return this.$initials(this.profile?.fullName);
        },

        profileUrlValue() {
            return this.profile.profileImageUrl || "";
        },

        socialsArrayExists() {
            return this.profile?.links?.length > 0;
        },

        disabledModalLinkBtn() {
            return this.linkState === "edit_link_loading" || this.linkState === "add_link_loading";
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        generateProfileCTA() {
            if (this.accountPlan !== "plus") {
                return "Go to settings";
            }

            return "Generate";
        },

    },

    async mounted() {
        await this.handleFetchProfile();

        if (this.profile?.skills?.length) {
            this.skills = this.profile.skills;
        } else {
            this.skills = [];
        }
    },

    methods: {
        addSkill(item) {            
            if (!this.skillOptions.includes(item)) {
                this.skills = [ ...this.skills, item];
            }
        },

        searchSkills(newVal) {  
            debounce(() => {
                const initialList = this.profile.skills;

                if (val) {
                    const filteredList = initialList.filter(item => item.toLowerCase().includes(newVal));
                    this.skillOptions = filteredList;
                } else {
                    this.skillOptions = initialList;
                }
            }, 500, "search-list");
        },

        async manageSkills() {
            try {                
                // TO DO: check if an update was performed to avoid unnecessary calls
                this.manageSkillsLoading = true;

                const payload = {
                    skills: this.skills,
                };
                
                await this.$store.dispatch("auth/manageSkills", payload);
                this.modalAction = "";
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;                
                this.$toast.error(errMsg);
            } finally {
                this.manageSkillsLoading = false;
            }
        },

        toggleDelete() {
            this.showDeleteModal = !this.showDeleteModal;
        },

        openLinkNewPage(url) {
            if (!url) return;

            window.open(url, "_blank");
        },

        openLinkModal(item, type) {
            this.linkType = type;

            if (item) {
                this.modalLinkForm = item;
            }
            this.modalAction = "showLinkModal";
        },

        closeEditLinkModal() {
            this.modalLinkForm = {
                name: "",
                link: "",
            };
            this.modalAction = "";
        },

        async generatePublicUrl() {
            try {                
                this.generateLinkLoading = true;

                const payload = {
                    allowedFields: this.generateLinkForm.allowedFields,
                    expiresAt: this.generateLinkForm.expiresAt,
                };
                
                await this.$store.dispatch("auth/generatePublicUrl", payload);
                
                this.closeEditLinkModal();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;                
                this.$toast.error(errMsg);
            } finally {
                this.generateLinkLoading = false;
            }
        },

        async addLink() {            
            try {                
                this.linkState = "add_link_loading";

                const payload = {
                    name: this.modalLinkForm.name?.toLowerCase(),
                    url: this.modalLinkForm.url,
                };
                
                await this.$store.dispatch("auth/addLink", { payload, type: "socials" });
                
                this.closeEditLinkModal();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;                
                this.$toast.error(errMsg);
            } finally {
                this.linkState = "default";
            }
        },

        async addPortfolioLink() {            
            try {                
                this.linkState = "add_link_loading";

                const payload = {
                    name: this.linkForm.name,
                    link: this.linkForm.url,
                };
                
                await this.$store.dispatch("auth/addLink", { payload, type: "links" });
                this.linkForm = {
                    url: "",
                    name: "",
                };
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;                
                this.$toast.error(errMsg);
            } finally {
                this.linkState = "default";
            }
        },

        async editLink() {            
            try {                
                this.linkState = "edit_link_loading";

                const payload = {
                    name: this.linkType === "edit_socials" ? this.modalLinkForm.name.toLowerCase() : this.modalLinkForm.name,
                    url: this.modalLinkForm.url,
                };

                const type = this.linkType.includes("_")?.split("_")[1];

                await this.$store.dispatch("auth/updateLink", { payload, type });
                this.closeEditLinkModal();
            } catch (error) {
                const errMsg =error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.linkState = "default";
            }
        },

        async handleModalLinkSubmit() {
            if (this.linkType === "add_socials") {
                await this.addLink();
            } else {
                await this.editLink();
            }
        },

        async deleteLink(id, type) {            
            try {                                
                if (!id) {
                    this.$toast.error("Unable to delete link");
                    return;
                }

                this.linkIdToDelete = id;
                this.linkState = "edit_loading";

                await this.$store.dispatch("auth/deleteLink", { type, id });
            } catch (error) {
                console.log({ error });
                
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.linkState = "default";
            }
        },

        openAccountModal(val) {
            if (!val || typeof val !== "string") return;

            this.currentAccountModalSelection = val;
            this.modalAction = "showAccountModal";
        },

        closeAccountModal() {
            this.modalAction = "";
            this.currentAccountModalSelection = "";
        },

        async handleFetchProfile() {
            try {
                this.fetchUserErr = false;
                this.fetchUserErrMsg = "";
                this.fetchUserLoading = true;

                await this.$store.dispatch("auth/fetchProfile", { refresh: false });
            } catch (error) {
                this.fetchUserErr = true;
                this.fetchUserErrMsg = error?.response?.data?.message;
            } finally {
                this.fetchUserLoading = false;
            }
        },

        async handleDeleteProfile() {
            try {
                this.$store.dispatch("app/togglePageLoading", true);
                this.deleteLoading = true;

                await this.$store.dispatch("auth/deleteProfile");

                this.$toast.success("Your account has been set for deletion");
                this.$router.push("/auth/logout");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.deleteLoading = false;
            }
        },

        togglePasswordUpdateModal() {
            this.showPasswordChangeModal = !this.showPasswordChangeModal;
        },

        async handleUpdateProfile(payload) {
            try {
                this.uploadProfileUrlLoading = "loading";

                await this.$store.dispatch("auth/updateProfile", payload);
                this.$toast.success("Profile updated");
                this.closeAccountModal();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.uploadProfileUrlLoading = false;
            }
        },

        async uploadToS3(signedUrl, file) {
            try {            
                const s3UploadRes = await fetch(signedUrl, {
                    method: "PUT",
                    headers: { "Content-Type": file.type },
                    body: file,
                });

                if (!s3UploadRes.ok) {
                    // Handle S3-specific errors (often plain text or XML)
                    const errorText = await s3UploadRes.text();                    
                    throw new Error(`Failed to upload to S3. Status: ${s3UploadRes.status}, Error: ${errorText}`);
                }

                return s3UploadRes;
            } catch (error) {
                console.error("Error during S3 upload:", error);
                throw error;
            }
        },

        async uploadProfileImage(file) {            
            try {         
                if (!file) {     
                    throw new Error("No file selected");       
                }

                const fileSize = Math.round(file.size / 1024) || 0;

                if (fileSize > 1000) {
                    throw new Error("Image greater than 1MB not allowed");       
                }

                this.uploadProfileUrlLoading = "loading";
                
                // make a call to the signed url
                const signedUrlRes = await this.$store.dispatch("auth/generateSignedUrl", { 
                    payload: {
                        file: {
                            name: file.name,
                            type: file.type,
                        },
                    },
                    params: { field: "profile" },
                });

                if (!signedUrlRes.signedUrl) {
                    throw new Error("Unable to upload image"); 
                }

                // leave this for now
                if (signedUrlRes.signedUrl.includes("localhost")) {
                    const res = await this.$store.dispatch("auth/validateMockSignedUrl", { 
                        payload: this.createFormData({ signedUrl: signedUrlRes.publicUrl, zak: signedUrlRes.key, file }),
                        config: {
                            headers: { "Content-Type": file.type },
                            // onUploadProgress: e => {
                            //     progress.value = Math.round((e.loaded / e.total) * 100);
                            // },
                        },
                    });
                    
                    if (!res) {
                        throw new Error("Unable to complete image upload");
                    }
                } else {
                    await this.uploadToS3(signedUrlRes.signedUrl, file);
                    await this.$store.dispatch("auth/updateProfile", {
                        profileImageUrl: signedUrlRes.publicUrl,
                    });
                }

                this.$toast.success("Profile image updated");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.uploadProfileUrlLoading = "default";   
            }
        },

        createFormData(obj) {            
            const formData = new FormData();
            
            Object.entries(obj).forEach(([k, v]) => {
                if (v instanceof File) formData.append(k, v);
                else formData.append(k, v);
            });

            return formData;
        },

        async removeProfileImage() {            
            try {         
                this.uploadProfileUrlLoading = "loading";

                await this.$store.dispatch("auth/removeUpload", { url: this.profileUrlValue });

                this.$toast.success("Profile image updated successfully");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.uploadProfileUrlLoading = "default";   
            }
        },
    },
};
</script>

<style scoped lang="scss">
    .alert {
        padding: 15px 10px;
        border-radius: 6px;

        &--info {
            background: #EDF2F7;
        }

        &--title {
            color: #1A202C;
            font-size: 0.875rem;
            font-weight: 600;
        }

        &--subtitle {
            color: #718096;
            font-size: 0.875rem;
        }
    
    }
</style>